import { useState } from 'react';
import { HandThumbUpIcon, HandThumbDownIcon, CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { apiClient } from '@lib/api/client';
import { captureScreenshotBase64 } from '../lib/captureScreenshot';

/**
 * Toast en la esquina (abajo-derecha) que PERSISTE tras cerrar el modal: pregunta
 * si la solución funcionó. El 👍 valida y crea el playbook; el 👎 lo registra.
 */
export function SupportFeedbackToast({
  interactionId,
  topic,
  onClose,
}: {
  interactionId: string;
  topic?: string;
  onClose: () => void;
}) {
  const [done, setDone] = useState<'up' | 'down' | null>(null);
  const [sending, setSending] = useState<'up' | 'down' | null>(null);

  async function vote(helpful: 'up' | 'down') {
    if (sending || done) return;
    setSending(helpful);
    const data_base64 = await captureScreenshotBase64();
    try {
      await apiClient.post('/support/feedback', {
        interactionId,
        helpful,
        image: data_base64 ? { mime_type: 'image/jpeg', data_base64 } : undefined,
      });
    } catch {
      // best-effort
    } finally {
      setSending(null);
      setDone(helpful);
      setTimeout(onClose, 2800);
    }
  }

  return (
    // Barra compacta y centrada abajo: transitoria y fuera del contenido (no tapa
    // tarjetas ni columnas como pasaba con el card ancho de la esquina). Una sola fila.
    <div
      data-support-ui
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      className="fixed bottom-6 left-1/2 z-[80] w-max max-w-[92vw] -translate-x-1/2 rounded-3xl border border-secondary-200 bg-white px-4 py-2.5 shadow-hard animate-in fade-in slide-in-from-bottom-4"
    >
      {done ? (
        <div className="flex items-center gap-2 text-sm text-secondary-700">
          <CheckCircleIcon className="h-5 w-5 shrink-0 text-primary-600" />
          {done === 'up' ? '¡Gracias! Lo guardamos para ayudar más rápido la próxima.' : 'Gracias. Lo registramos para revisarlo.'}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
          <p className="text-sm font-medium text-secondary-900">
            ¿Te funcionó {topic ? <span className="font-semibold">«{topic}»</span> : 'la solución'}?
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => vote('up')}
              disabled={!!sending}
              className="inline-flex items-center gap-1.5 rounded-full bg-primary-700 px-3 py-1.5 text-sm font-semibold text-white transition hover:bg-primary-800 disabled:opacity-60"
            >
              <HandThumbUpIcon className="h-4 w-4" /> Sí
            </button>
            <button
              type="button"
              onClick={() => vote('down')}
              disabled={!!sending}
              className="inline-flex items-center gap-1.5 rounded-full border border-secondary-300 px-3 py-1.5 text-sm text-secondary-700 transition hover:bg-secondary-50 disabled:opacity-60"
            >
              <HandThumbDownIcon className="h-4 w-4" /> No
            </button>
            <button
              onClick={onClose}
              className="ml-0.5 rounded-full p-1 text-secondary-400 transition hover:bg-secondary-50 hover:text-secondary-700"
              aria-label="Cerrar"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
